import type { ErrorDetails, ErrorResponse } from "@shared/types";
import type { AxiosError } from "axios";

const fallbackMessages = {
	unknown: "Noma'lum xatolik yuz berdi.",
	request: "So'rovda xatolik yuz berdi.",
	unauthorized: "Sessiya muddati tugadi. Qayta kirish kerak.",
	forbidden: "Sizda bu amalni bajarish uchun ruxsat yo'q.",
	server: "Server xatoligi. Keyinroq urinib ko'ring.",
	validation: "Kiritilgan ma'lumotlar noto'g'ri.",
	conflict: "Bu ma'lumot allaqachon mavjud.",
	notFound: "Ma'lumot topilmadi.",
	invalidCredentials: "Login yoki parol noto'g'ri.",
	invalidOperation: "Bu amalni bajarib bo'lmaydi.",
	rateLimit: "Juda ko'p so'rov yuborildi. Biroz kuting.",
} as const;

export function parseApiError(error: unknown): string {
	if (!isAxiosError(error)) {
		return fallbackMessages.unknown;
	}

	const responseData = error.response?.data;

	if (!(responseData && isErrorResponse(responseData))) {
		if (hasPlainMessage(responseData)) {
			return responseData.message;
		}
		return parseHttpError(error);
	}

	const { code, message, details } = responseData.error;

	switch (code) {
		case "VALIDATION_ERROR":
		case "INVALID_INPUT":
		case "MISSING_FIELD":
		case "INVALID_FORMAT": {
			return parseValidationError(message, details);
		}

		case "ALREADY_EXISTS":
		case "DUPLICATE_ENTRY":
		case "CONFLICT":
			return message || fallbackMessages.conflict;

		case "NOT_FOUND":
		case "RESOURCE_NOT_FOUND":
		case "USER_NOT_FOUND":
			return message || fallbackMessages.notFound;

		case "FORBIDDEN":
		case "INSUFFICIENT_PERMISSIONS":
		case "ACCESS_DENIED":
			return fallbackMessages.forbidden;

		case "UNAUTHORIZED":
		case "INVALID_TOKEN":
		case "TOKEN_EXPIRED":
			return fallbackMessages.unauthorized;

		case "INVALID_CREDENTIALS":
			return fallbackMessages.invalidCredentials;

		case "UNPROCESSABLE_ENTITY":
		case "BUSINESS_RULE_VIOLATION":
		case "INVALID_OPERATION":
		case "INVALID_STATE":
			return message || fallbackMessages.invalidOperation;

		case "RATE_LIMIT_EXCEEDED":
			return fallbackMessages.rateLimit;

		case "INTERNAL_ERROR":
		case "DATABASE_ERROR":
		case "EXTERNAL_SERVICE_ERROR":
			return fallbackMessages.server;

		default:
			if (
				(responseData.error as Record<string, unknown>).name === "ZodError" ||
				(responseData.error as Record<string, unknown>).issues
			) {
				const issues =
					((responseData.error as Record<string, unknown>).issues as Record<string, unknown>[]) ||
					[];
				const details = issues.map((i) => ({
					field: Array.isArray(i.path) ? i.path.join(".") : undefined,
					reason: typeof i.message === "string" ? i.message : undefined,
				}));
				return parseValidationError("Validatsiya xatosi", details);
			}
			return message || `Xatolik: ${code}`;
	}
}

function parseHttpError(error: AxiosError): string {
	if (error.response?.status === 401) {
		return fallbackMessages.unauthorized;
	}
	if (error.response?.status === 403) {
		return fallbackMessages.forbidden;
	}
	if (error.response && error.response.status >= 500) {
		return fallbackMessages.server;
	}
	return error.message || fallbackMessages.request;
}

function parseValidationError(message: string, details?: ErrorDetails[]): string {
	if (!details?.length) {
		return message || fallbackMessages.validation;
	}

	const messages = details.map(formatErrorDetails).join("; ");
	return `Validatsiya xatosi: ${messages}`;
}

function formatErrorDetails(details: ErrorDetails): string {
	const field = details.field ? `${details.field}: ` : "";
	return `${field}${details.reason || "Noto'g'ri qiymat"}`;
}

function isAxiosError(error: unknown): error is AxiosError<{ message?: string }> {
	return (
		typeof error === "object" &&
		error !== null &&
		"isAxiosError" in error &&
		(error as AxiosError).isAxiosError === true
	);
}

function isErrorResponse(response: unknown): response is ErrorResponse {
	return (
		typeof response === "object" &&
		response !== null &&
		"success" in response &&
		(response as ErrorResponse).success === false &&
		"error" in response
	);
}

/**
 * Simple API error message extraction.
 * Returns the backend error message or a fallback string.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
	if (isAxiosError(error) && error.response?.data?.message) {
		return error.response.data.message as string;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return fallback;
}

function hasPlainMessage(response: unknown): response is { message: string } {
	return (
		typeof response === "object" &&
		response !== null &&
		"message" in response &&
		typeof (response as { message?: unknown }).message === "string" &&
		(response as { message: string }).message.trim().length > 0
	);
}
